import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LineupImportsBatchStatus } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useCancelImportLineup } from './useCancelImportLineup';
import { useConfirmImportLineup } from './useConfirmImportLineup';

const Root = styled(Horizontal)`
  justify-content: flex-end;
  padding: var(--double-unit);
  border-top: 1px solid var(--c-neutral-400);
`;

type Props = {
  id?: string | null;
  lineupsCount?: number;
  lineupsImportState?: So5LineupImportsBatchStatus;
  onClose: () => void;
  onConfirm: () => void;
  onComplete: () => void;
};

export const ImportLineupActions = ({
  id,
  lineupsCount,
  lineupsImportState,
  onClose,
  onConfirm,
  onComplete,
}: Props) => {
  const confirmImportLineup = useConfirmImportLineup(id);
  const cancelImportLineup = useCancelImportLineup(id);

  if (!id || !lineupsCount || !lineupsImportState) return null;

  const isReady = [
    So5LineupImportsBatchStatus.PREPARED,
    So5LineupImportsBatchStatus.PREPARING,
  ].includes(lineupsImportState);

  const isLoading =
    So5LineupImportsBatchStatus.PROCESSING === lineupsImportState;
  const isComplete =
    So5LineupImportsBatchStatus.PROCESSED === lineupsImportState;

  return (
    <Root>
      {isReady && (
        <Button
          size="medium"
          onClick={() => {
            cancelImportLineup();
            onClose();
          }}
          color="tertiary"
        >
          <FormattedMessage {...glossary.cancel} />
        </Button>
      )}
      <LoadingButton
        loading={So5LineupImportsBatchStatus.PREPARING === lineupsImportState}
        size="medium"
        color="secondary"
        onClick={() => {
          if (isReady) {
            confirmImportLineup();
            onConfirm();
          }
          if (isComplete) {
            onClose();
            onComplete();
          }
        }}
      >
        {isReady && (
          <FormattedMessage
            defaultMessage="Import {lineupsCount} {lineupsCount, plural, one {lineup} other {lineups}}"
            id="importLineup.actions.label.ready"
            values={{ lineupsCount }}
          />
        )}

        {isLoading && <FormattedMessage {...glossary.loading} />}
        {isComplete && <FormattedMessage {...glossary.gotIt} />}
      </LoadingButton>
    </Root>
  );
};
